import * as service from "../service";
import { getEventDateRange } from "@/utils/eventDateFilter";
import { store } from "../../store";
import type { ServiceResponse } from "../types";

export type GetEventsParams = {
  page?: number;
  postType?: string[];
  society?: { id: string }[];
  university?: string[];
  datePreset?: string;
  openOnly?: boolean;
  location?: string;
  hashtag?: string;
};

/**
 * Shared shapes returned by every event feed endpoint
 * (`getEvents`, `getEventsMonthly`, `getSubscriptedList`, `searchEvents`).
 * The API stores loosely-typed fields as JSON columns, so they arrive as
 * arrays of strings or `{ input1, input2 }` label/value pairs.
 */
export type EventContact = { input1?: string | null; input2?: string | null } | string;

/** A `{ label, value }` pair as stored in the `phones` / `emails` / `links` columns. */
export type EventContactPair = { input1: string; input2: string };

/** A `bachelor_degrees` row, as selected in the event target step. */
export type BachelorDegree = {
  id?: string;
  name_en?: string | null;
  name_zh_hant?: string | null;
};

export type EventDetails = {
  id: string;
  key_id?: string | null;
  title?: string | null;
  title_en?: string | null;
  description?: string | null;
  description_en?: string | null;
  location?: string | null;
  location_en?: string | null;
  event_images?: unknown;
  phones?: unknown;
  emails?: unknown;
  links?: unknown;
  hash_tags?: unknown;
  start_date?: string | null;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  deadline_date?: string | null;
  deadline_time?: string | null;
  allow_join_event?: boolean | number | null;
  university?: unknown;
  society_id?: unknown;
  source_url?: string | null;
  post_type?: string | null;
  who_can_join?: string | null;
  who_can_join_en?: string | null;
  all_day?: number | null;
  never?: number | null;
  daily_repeat?: number | null;
  weekly_repeat?: number | null;
  end_date_on?: string | null;
  repeat_type?: string | null;
  original_start_date?: string | null;
  public?: number | null;
};

export type EventOrganizer = {
  id: string;
  society_icon?: string | null;
  name_en?: string | null;
  name_zh_hant?: string | null;
  instagram_username?: string | null;
  following?: boolean;
};

export type StudentEvent = {
  details: EventDetails;
  organizers: EventOrganizer[];
  subscribed: boolean;
};

/**
 * Form state of the create / edit event wizards. It doubles as the creation
 * payload: `imageUrls` holds cover images which are either already hosted or
 * still `data:` URLs — the organizer services upload the latter to Cloudinary
 * before submitting.
 */
export type EventFormPayload = {
  societyIds: string[];
  universityCodes: string[];
  imageUrls: string[];
  title: string;
  description: string;
  location: string;
  phoneList: EventContactPair[];
  emailList: EventContactPair[];
  linkList: EventContactPair[];
  tagList: string[];
  startDate: string;
  endDate: string;
  allDay: boolean;
  startTime: string;
  endTime: string;
  neverRepeat: boolean;
  dailyRepeat: boolean;
  weeklyRepeat: boolean;
  endDateOn: string;
  deadlineDate: string;
  deadlineTime: string;
  allowJoin: boolean;
};

/**
 * Serialize a wizard payload into the body `api/organizer/events/uploadEvent`
 * and `api/organizer/dashboard/editEvent` expect. Mirrors the RN organizer
 * services: `allow_join_event` / `all_day` are 0-1 flags where 0 means
 * "enabled" / "all day". The repeat flags are the exception — the backend
 * derives `repeat_type` as
 * `daily_repeat ? "daily" : weekly_repeat ? "weekly" : "never"`, so they are
 * truthy only when that repeat mode is actually selected.
 */
export const toEventFormBody = (payload: EventFormPayload) => ({
  society_id: payload.societyIds,
  event_images: payload.imageUrls,
  title: payload.title,
  description: payload.description,
  location: payload.location,
  phone: payload.phoneList,
  email: payload.emailList,
  link: payload.linkList,
  hash_tags: payload.tagList,
  start_date: payload.startDate,
  end_date: payload.endDate,
  all_day: payload.allDay ? 0 : 1,
  start_time: payload.allDay ? "" : payload.startTime,
  end_time: payload.allDay ? "" : payload.endTime,
  never: payload.neverRepeat ? 0 : 1,
  daily_repeat: payload.neverRepeat ? 0 : payload.dailyRepeat ? 1 : 0,
  weekly_repeat: payload.neverRepeat ? 0 : payload.weeklyRepeat ? 1 : 0,
  end_date_on: payload.neverRepeat ? "" : payload.endDateOn,
  deadline_date: payload.deadlineDate,
  deadline_time: payload.deadlineDate ? payload.deadlineTime : "",
  allow_join_event: payload.allowJoin ? 0 : 1,
  university: payload.universityCodes,
});

/**
 * Fetch the paginated event feed for the current student. The society /
 * university filters default to the auth slice; `postType` (and explicit
 * society / university values) narrow the feed further, e.g. for the filter
 * bar of the student home page.
 * Backend: GET /api/user/events/getEvents
 */
export const GetEventsAPI = async ({
  page = 1,
  postType,
  society,
  university,
  datePreset,
  openOnly,
  location,
  hashtag,
}: GetEventsParams = {}): Promise<ServiceResponse<StudentEvent[]>> => {
  const auth = store.getState().auth as any;
  const societyParam =
    society && society.length > 0 ? society : auth.filterSociety;
  const universityParam =
    university && university.length > 0 ? university : auth.filterUniversity;
  const dateRange = getEventDateRange(datePreset);
  try {
    const resp = await service.call("GET", "api/user/events/getEvents", {
      society: societyParam,
      university: universityParam,
      page,
      ...dateRange,
      ...(postType && postType.length > 0 ? { post_type: postType } : {}),
      ...(openOnly ? { open_only: 1 } : {}),
      ...(location?.trim() ? { location: location.trim() } : {}),
      ...(hashtag?.trim()
        ? { hashtag: hashtag.trim().replace(/^#/, "") }
        : {}),
      ...(auth.user?.id ? { user_id: auth.user.id } : {}),
    });
    return resp;
  } catch (err) {
    console.error(`GetEventsAPI ${err}`);
    return { status: -1 };
  }
};

/**
 * Fetch the events the current student has subscribed to.
 * Backend: GET /api/user/subscripted/getSubscriptedList
 */
export const GetSubscriptedEventsAPI = async (
  page: number = 1,
): Promise<ServiceResponse<StudentEvent[]>> => {
  const { user } = store.getState().auth as any;
  try {
    const resp = await service.call(
      "GET",
      "api/user/subscripted/getSubscriptedList",
      {
        page,
        user_id: user?.id,
      },
    );
    return resp;
  } catch (err) {
    console.error(`GetSubscriptedEventsAPI ${err}`);
    return { status: -1 };
  }
};

/**
 * Fetch the events uploaded to the given societies — i.e. the "my created
 * events" list of an organizer. The society ids must be the verified
 * memberships of the current student.
 * Backend: GET /api/user/events/getUploadedEvents
 */
export const GetUploadedEventsAPI = async (
  societyIds: string[],
  page: number = 1,
): Promise<ServiceResponse<StudentEvent[]>> => {
  const { user } = store.getState().auth as any;
  if (societyIds.length === 0) return { status: 0, result: [] };
  try {
    const resp = await service.call(
      "GET",
      "api/user/events/getUploadedEvents",
      {
        user_id: user?.id,
        society_id: societyIds,
        page,
      },
    );
    return resp;
  } catch (err) {
    console.error(`GetUploadedEventsAPI ${err}`);
    return { status: -1 };
  }
};
